import type { SearchTileDensity } from '../searchTileDensity'

const DEFAULT_GROUPED_HOTEL_INCLUSIONS = 3
const COMPACT_HOTEL_INCLUSIONS = 3
const COMPACT_GROUPED_HOTEL_INCLUSIONS = 2
/** CRO compact shows up to six inclusion or facility rows. Cancellation is an extra first row. */
export const CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES = 6

export function getCompactInclusionListLimit(maxMappedCategories: number, hasCancellationRow: boolean): number {
  return hasCancellationRow ? maxMappedCategories + 1 : maxMappedCategories
}

type UrgencyTagVariant = 'compact' | 'default'

interface AccommodationSearchTileView {
  readonly density: SearchTileDensity
  readonly isCompact: boolean
  readonly showMeta: boolean
  readonly showCancellationPolicy: boolean
  readonly showUpgradeBanner: boolean
  readonly showFeatureTitle: boolean
  readonly showSecondaryLuxPlusPromos: boolean
  readonly useCompactPricing: boolean
  /**
   * Horizontal alignment of the price stack after the CTA is removed.
   * @default start
   */
  readonly pricingAlign: 'start' | 'end'
  /**
   * Member-price heading scale for compact experiment pricing.
   * CRO mosaic uses `M` (heading5, 16px on mobile).
   * @default L
   */
  readonly compactPriceSize: 'S' | 'M' | 'L'
  readonly hotelMaxTotalInclusions?: number
  readonly groupedHotelMaxTotalInclusions: number
  readonly bedbankMaxInclusions?: number
  readonly hideBedbankLuxPlusPricing: boolean
  readonly hideIncludesTaxesAndFees: boolean
  readonly showLocationOverline: boolean
  readonly showLocationDistanceLabels: boolean
  readonly urgencyTagVariant: UrgencyTagVariant
  readonly hideBookmark: boolean
  readonly hideMapLink: boolean
  readonly hideRoomBreakdown: boolean
  readonly hideMemberWalls: boolean
  /** Hide the pricing-area View offer / Select dates button; the tile row stays the click target. */
  readonly hideAction: boolean
  readonly showImageMosaic: boolean
  /** Hide LTLE / LTS product-type tags on the tile (used by cro-compact mosaics). */
  readonly hideProductLabel: boolean
  readonly showFreeCancellationAsInclusion: boolean
  /** Société eSIM / loyalty benefit rows on the search tile. */
  readonly showLoyaltyInclusions: boolean
  /** Map raw inclusion copy to Search Inclusions filter category labels. */
  readonly mapInclusionsToSearchFilterCategories: boolean
  readonly titleLineClamp: number
}

const DEFAULT_ACCOMMODATION_SEARCH_TILE_VIEW: AccommodationSearchTileView = {
  density: 'default',
  isCompact: false,
  showMeta: true,
  showCancellationPolicy: true,
  showUpgradeBanner: true,
  showFeatureTitle: true,
  showSecondaryLuxPlusPromos: true,
  useCompactPricing: false,
  pricingAlign: 'start',
  compactPriceSize: 'L',
  groupedHotelMaxTotalInclusions: DEFAULT_GROUPED_HOTEL_INCLUSIONS,
  hideBedbankLuxPlusPricing: false,
  hideIncludesTaxesAndFees: false,
  showLocationOverline: true,
  showLocationDistanceLabels: true,
  urgencyTagVariant: 'default',
  hideBookmark: false,
  hideMapLink: false,
  hideRoomBreakdown: false,
  hideMemberWalls: false,
  hideAction: false,
  showImageMosaic: false,
  hideProductLabel: false,
  showFreeCancellationAsInclusion: false,
  showLoyaltyInclusions: true,
  mapInclusionsToSearchFilterCategories: false,
  titleLineClamp: 3,
}

const COMPACT_ACCOMMODATION_SEARCH_TILE_VIEW: AccommodationSearchTileView = {
  density: 'compact',
  isCompact: true,
  showMeta: false,
  showCancellationPolicy: false,
  showUpgradeBanner: false,
  showFeatureTitle: false,
  showSecondaryLuxPlusPromos: false,
  useCompactPricing: true,
  pricingAlign: 'start',
  compactPriceSize: 'L',
  hotelMaxTotalInclusions: COMPACT_HOTEL_INCLUSIONS,
  groupedHotelMaxTotalInclusions: COMPACT_GROUPED_HOTEL_INCLUSIONS,
  hideBedbankLuxPlusPricing: true,
  hideIncludesTaxesAndFees: true,
  showLocationOverline: true,
  showLocationDistanceLabels: false,
  urgencyTagVariant: 'compact',
  hideBookmark: false,
  hideMapLink: false,
  hideRoomBreakdown: false,
  hideMemberWalls: false,
  hideAction: false,
  showImageMosaic: false,
  hideProductLabel: false,
  showFreeCancellationAsInclusion: false,
  showLoyaltyInclusions: true,
  mapInclusionsToSearchFilterCategories: false,
  titleLineClamp: 3,
}

const CRO_COMPACT_ACCOMMODATION_SEARCH_TILE_VIEW: AccommodationSearchTileView = {
  density: 'cro-compact',
  isCompact: true,
  showMeta: false,
  showCancellationPolicy: false,
  showUpgradeBanner: false,
  showFeatureTitle: false,
  showSecondaryLuxPlusPromos: false,
  useCompactPricing: true,
  pricingAlign: 'end',
  compactPriceSize: 'M',
  hotelMaxTotalInclusions: CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES,
  groupedHotelMaxTotalInclusions: CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES,
  bedbankMaxInclusions: CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES,
  hideBedbankLuxPlusPricing: true,
  hideIncludesTaxesAndFees: true,
  showLocationOverline: true,
  showLocationDistanceLabels: false,
  urgencyTagVariant: 'compact',
  hideBookmark: true,
  hideMapLink: true,
  hideRoomBreakdown: true,
  hideMemberWalls: true,
  hideAction: true,
  showImageMosaic: true,
  hideProductLabel: true,
  showFreeCancellationAsInclusion: true,
  // Keep mosaic rows focused on hotel inclusions; hide Société eSIM benefit clutter.
  showLoyaltyInclusions: false,
  mapInclusionsToSearchFilterCategories: true,
  titleLineClamp: 2,
}

export function getAccommodationSearchTileView(density: SearchTileDensity): AccommodationSearchTileView {
  switch (density) {
    case 'compact':
      return COMPACT_ACCOMMODATION_SEARCH_TILE_VIEW
    case 'cro-compact':
      return CRO_COMPACT_ACCOMMODATION_SEARCH_TILE_VIEW
    default:
      return DEFAULT_ACCOMMODATION_SEARCH_TILE_VIEW
  }
}
